import { useAuthStore } from '@/features/auth';

export interface Permission {
  action: string;
  resource: string;
}

export const PERMISSIONS = {
  // API Management
  VIEW_API: { action: 'read', resource: 'api' },
  MANAGE_API: { action: 'manage', resource: 'api' },
  CONFIGURE_API: { action: 'configure', resource: 'api' },
  
  // Audit & Logs
  VIEW_AUDIT_LOGS: { action: 'read', resource: 'audit_logs' },
  VIEW_SALESFORCE_RESPONSE: { action: 'read', resource: 'salesforce_response' },
  EXPORT_AUDIT_LOGS: { action: 'export', resource: 'audit_logs' },
  MANAGE_CRON_JOBS: { action: 'manage', resource: 'cron_jobs' },
  
  // Queue Management
  VIEW_QUEUE_STATUS: { action: 'read', resource: 'queue' },
  MANAGE_QUEUE: { action: 'manage', resource: 'queue' },
  FORCE_FLUSH_QUEUE: { action: 'force_flush', resource: 'queue' },
  
  // User Management
  VIEW_USERS: { action: 'read', resource: 'users' },
  MANAGE_USERS: { action: 'manage', resource: 'users' },
  VIEW_API_KEYS: { action: 'read', resource: 'api_keys' },
  MANAGE_API_KEYS: { action: 'manage', resource: 'api_keys' },
  
  // System
  VIEW_SYSTEM_HEALTH: { action: 'read', resource: 'system' },
  MANAGE_SYSTEM: { action: 'manage', resource: 'system' },
} as const;

export const ROLE_PERMISSIONS = {
  SUPER_ADMIN: Object.values(PERMISSIONS),
  ADMIN: Object.values(PERMISSIONS),
  USER: [
    PERMISSIONS.VIEW_QUEUE_STATUS,
    PERMISSIONS.VIEW_SALESFORCE_RESPONSE,
  ],
} as const;

function normalizeRole(role?: string): keyof typeof ROLE_PERMISSIONS | null {
  const upper = role?.toUpperCase();
  if (upper === 'SUPER_ADMIN') return 'SUPER_ADMIN';
  if (upper === 'ADMIN') return 'ADMIN';
  if (upper === 'USER') return 'USER';
  return null;
}

export function usePermissions() {
  const { user } = useAuthStore();
  
  const isSuperAdmin = user?.role?.toUpperCase() === 'SUPER_ADMIN';
  // NEVER grant full access when there is no user — including in development
  const hasFullAccess = !!user && isSuperAdmin;

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    if (hasFullAccess) return true;
    
    const normalizedRole = normalizeRole(user.role);
    if (!normalizedRole) return false;

    const userPermissions = ROLE_PERMISSIONS[normalizedRole] || [];
    return userPermissions.some(p => 
      p.action === permission.action && p.resource === permission.resource
    );
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const canAccess = (resource: string, action: string): boolean => {
    return hasPermission({ action, resource });
  };

  const roleUpper = user?.role?.toUpperCase();

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    userRole: user?.role,
    isSuperAdmin: isSuperAdmin,
    isAdmin: roleUpper === 'ADMIN',
    isUser: roleUpper === 'USER',
  };
}
